import React, { Suspense } from 'react';

import { useFormContext } from 'react-hook-form';

import { ErrorBoundaryWithRetry } from '@/_components';
import { ApiError, snackbar } from '@/_shared';

import { useValidateAddressMutation } from './api';
import { AddressPreview } from './components/address-preview';
import { AddressValidationModal, type EnteredAddressSnapshot } from './components/validation-modal';
import Layout from './layout';
import Loading from './loading';
import type {
  AddressQuerySchema,
  AddressValidationRequest,
  AddressValidationResponse,
  RecommendedAction,
  ValidationDecision,
} from './shared';
import type { AddressFormHandle, AddressProps, AddressValidationState, PendingAddressUpdate } from './types';

const Page = React.lazy(() => import('./components/address-form'));

function buildRequestFromQuery(addr: AddressQuerySchema): AddressValidationRequest {
  return {
    address1: addr.address1 || '',
    address2: addr.address2 || null,
    city: addr.city || null,
    postal_code: addr.postal_code || null,
    administrative_area: addr.state?.abbreviation ?? addr.state?.name ?? null,
    country_code: addr.country?.short_name ?? null,
  };
}

function buildSnapshotFromQuery(addr: AddressQuerySchema): EnteredAddressSnapshot {
  return {
    address1: addr.address1 || '',
    address2: addr.address2 || '',
    city: addr.city || '',
    state: addr.state?.abbreviation ?? addr.state?.name ?? '',
    postalCode: addr.postal_code || '',
    country: addr.country?.name || '',
  };
}

function initValidationFromQuery(value: AddressQuerySchema | undefined) {
  const status = value?.usps_validation_status;
  if (status === 'verified') return { action: 'PROCEED' as const, decision: 'use_recommended' as const };
  return { action: null, decision: 'pending' as const };
}

function hasMinFieldsFromQuery(addr: AddressQuerySchema | undefined): boolean {
  if (!addr) return false;
  return !!(addr.address1 && addr.postal_code && addr.city && addr.state?.id && addr.country?.id);
}

function AddressBase({
  text,
  icon,
  isEdit,
  onRequestEdit,
  onChange,
  disabled = false,
  protectedIds,
  noLayout = false,
  nameKey = 'addresses',
  ...formProps
}: AddressProps) {
  const form = useFormContext();

  const { mutateAsync: validateAddressApi, isPending: isValidatingAddress } = useValidateAddressMutation();
  const [validationResult, setValidationResult] = React.useState<AddressValidationResponse | null>(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = React.useState(false);
  const initial = initValidationFromQuery(formProps.value);
  const [validationAction, setValidationAction] = React.useState<RecommendedAction | null>(initial.action);
  const [validationDecision, setValidationDecision] = React.useState<ValidationDecision>(initial.decision);
  const [enteredAddressSnapshot, setEnteredAddressSnapshot] = React.useState<EnteredAddressSnapshot | null>(null);
  const pendingUpdateRef = React.useRef<PendingAddressUpdate | null>(null);
  const [editModeCanValidate, setEditModeCanValidate] = React.useState(false);
  const setCanValidateRef = React.useRef<((v: boolean) => void) | null>(null);
  setCanValidateRef.current = setEditModeCanValidate;

  const addressFormRef = React.useRef<AddressFormHandle>(null);
  const [showUpdateReminder, setShowUpdateReminder] = React.useState(false);

  const resetValidation = React.useCallback(() => {
    setValidationAction(null);
    setValidationDecision('pending');
    setValidationResult(null);
  }, []);

  React.useEffect(() => {
    if (validationResult) return;
    const { action, decision } = initValidationFromQuery(formProps.value);
    setValidationAction(action);
    setValidationDecision(decision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProps.value?.usps_validation_status]);

  const handleValidateAddress = React.useCallback(async () => {
    let request: AddressValidationRequest;
    let snapshot: EnteredAddressSnapshot;

    if (addressFormRef.current) {
      const data = addressFormRef.current.getFormData();
      request = data.request;
      snapshot = data.snapshot;
    } else if (formProps.value?.address1) {
      request = buildRequestFromQuery(formProps.value);
      snapshot = buildSnapshotFromQuery(formProps.value);
    } else {
      return;
    }

    setEnteredAddressSnapshot(snapshot);

    try {
      const result = await validateAddressApi(request);
      setValidationResult(result);
      const action = result.recommended_action;
      setValidationAction(action);

      switch (action) {
        case 'PROCEED':
          setValidationDecision('pending');
          setIsValidationModalOpen(true);
          break;
        case 'CONFIRM':
          setValidationDecision('pending');
          setIsValidationModalOpen(true);
          break;
        case 'CONFIRM_ADD_SUBPREMISES':
          setValidationDecision('pending');
          setIsValidationModalOpen(true);
          break;
        case 'FIX':
        default:
          setValidationDecision('pending');
          setIsValidationModalOpen(true);
          break;
      }
    } catch (error: unknown) {
      setValidationResult({
        address_validated: false,
        recommended_action: 'FIX',
        formatted_address: null,
        standardized_address: null,
        geocode: null,
        verdict: null,
        address_components: [],
      });
      setValidationAction('FIX');
      setValidationDecision('pending');
      setIsValidationModalOpen(true);

      if (error instanceof ApiError) {
        snackbar.error(error.message || 'Address validation failed.');
      }
    }
  }, [formProps.value, validateAddressApi]);

  const handleKeepCurrent = React.useCallback(() => {
    setValidationDecision('keep_current');
    setIsValidationModalOpen(false);

    if (addressFormRef.current) {
      addressFormRef.current.markValidationDirty();
    } else {
      pendingUpdateRef.current = { keepCurrent: true };
      onRequestEdit();
    }
  }, [onRequestEdit]);

  const handleUseRecommended = React.useCallback(() => {
    setValidationDecision('use_recommended');
    setIsValidationModalOpen(false);
    setShowUpdateReminder(true);

    if (addressFormRef.current) {
      addressFormRef.current.applyRecommended();
    } else {
      pendingUpdateRef.current = { applyRecommended: true };
      onRequestEdit();
    }
  }, [onRequestEdit]);

  const handleUseRecommendedWithUnit = React.useCallback(
    (unitNumber: string) => {
      setValidationDecision('use_recommended');
      setIsValidationModalOpen(false);
      setShowUpdateReminder(true);

      if (addressFormRef.current) {
        addressFormRef.current.applyRecommended();
        if (unitNumber.trim()) {
          addressFormRef.current.applyUnit(unitNumber.trim());
        }
      } else {
        pendingUpdateRef.current = { applyRecommended: true, unitNumber: unitNumber.trim() || undefined };
        onRequestEdit();
      }
    },
    [onRequestEdit]
  );

  const validationState: AddressValidationState = React.useMemo(
    () => ({
      validationAction,
      validationDecision,
      validationResult,
      onResetValidation: resetValidation,
      pendingUpdateRef,
      setCanValidateRef,
    }),
    [validationAction, validationDecision, validationResult, resetValidation]
  );

  React.useEffect(() => {
    if (!isEdit) {
      setShowUpdateReminder(false);
      // Re-derive validation state from persisted value when exiting edit mode
      // (handles cancel where field edits were reverted but local state was reset)
      if (!validationResult) {
        const { action, decision } = initValidationFromQuery(formProps.value);
        setValidationAction(action);
        setValidationDecision(decision);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const isValidated =
    validationAction !== null && validationAction !== 'FIX' && validationDecision === 'use_recommended';

  const canValidate = isEdit ? editModeCanValidate : hasMinFieldsFromQuery(formProps.value);

  const onChangeAddress = React.useCallback(
    (addresses: AddressQuerySchema[]) => {
      const updatedValues: Record<string, AddressQuerySchema> = {};
      [...(form.getValues(nameKey) || []), ...addresses].forEach((address) => {
        if (!(protectedIds || []).includes(address.id)) {
          updatedValues[address.id] = address;
        }
      });
      onChange(Object.values(updatedValues));
    },
    [form, onChange, protectedIds, nameKey]
  );

  return (
    <>
      <Layout
        text={text}
        isEdit={isEdit}
        icon={icon}
        noLayout={noLayout}
        value={formProps.value}
        canValidate={canValidate}
        isValidatingAddress={isValidatingAddress}
        isValidated={isValidated}
        showUpdateReminder={showUpdateReminder}
        onValidateAddress={handleValidateAddress}
      >
        {!disabled && isEdit ? (
          <ErrorBoundaryWithRetry>
            <Suspense fallback={<Loading />}>
              <Page
                ref={addressFormRef}
                {...formProps}
                isEdit
                onRequestEdit={onRequestEdit}
                onChange={onChangeAddress}
                protectedIds={protectedIds}
                validationState={validationState}
              />
            </Suspense>
          </ErrorBoundaryWithRetry>
        ) : (
          <AddressPreview value={formProps.value} />
        )}
      </Layout>
      <AddressValidationModal
        open={isValidationModalOpen}
        result={validationResult}
        enteredAddress={enteredAddressSnapshot}
        onClose={() => setIsValidationModalOpen(false)}
        onKeepCurrent={handleKeepCurrent}
        onUseRecommended={handleUseRecommended}
        onUseRecommendedWithUnit={handleUseRecommendedWithUnit}
        onEditAddress={() => {
          setIsValidationModalOpen(false);
          onRequestEdit();
        }}
      />
    </>
  );
}

export const Address = React.memo(AddressBase);
